import { useEffect } from 'react';
import { FieldValues, UseFormGetValues, UseFormReset } from 'react-hook-form';
import { isEqual } from 'es-toolkit';
import { usePrevious } from 'hooks/usePrevious';

type UseResetOnInitialValuesChangeProps<T extends FieldValues> = {
  values: T;
  getValues: UseFormGetValues<T>;
  reset: UseFormReset<T>;
  /**
   * Fields to preserve across resets. Must be a stable reference (e.g. defined
   * outside the component or wrapped in `useMemo`).
   *
   * Rich text editors keyed with `getIncidentRemountKey` must not be listed
   * here, or remove their key when adding them.
   */
  preserveFields?: (keyof T)[];
};

export const useResetOnInitialValuesChange = <T extends FieldValues>({
  values,
  getValues,
  reset,
  preserveFields = [],
}: UseResetOnInitialValuesChangeProps<T>): void => {
  const previousValues = usePrevious(values);

  useEffect(() => {
    // We intentionally use reset-based synchronization instead of react hook form's dynamic `values` option
    // because this flow needs to preserve specific transient fields while applying updated defaults.
    // The dynamic `values` option would rehydrate all fields directly and can overwrite those
    // user-controlled confirmations when upstream data is refreshed.
    if (previousValues && isEqual(previousValues, values)) {
      return;
    }

    // Keep selected transient form fields when we refresh defaults.
    const currentValues = getValues();
    const preservedValues = Object.fromEntries(
      preserveFields.map((field) => [field, currentValues[field]]),
    ) as Partial<T>;

    reset({
      ...values,
      ...preservedValues,
    });
  }, [getValues, preserveFields, previousValues, reset, values]);
};
